import type { Recipe } from '../types';

export const freezerBreakfastBurritos: Recipe = {
  slug: 'freezer-breakfast-burritos',
  title: 'Freezer Breakfast Burritos',
  meal: 'breakfast',
  category: 'meal',
  types: ['breakfast', 'burrito'],
  cuisines: ['mexican'],
  traits: ['freezer-friendly', 'make-ahead'],
  yield: '18 burritos',
  activeTime: '1 hr',
  totalTime: '1 hr 15 min',
  freezerLife: '3 months',
  difficulty: 2,

  summary:
    'Eighteen breakfast burritos built for the freezer. The tortillas are raw and get cooked first, which sounds like extra work and is the reason these reheat better than any store-bought version.',

  flavorNotes:
    'Straightforward and savory — sage-forward breakfast sausage, soft eggs, crisp potato, melted cheddar. Nothing here is unfamiliar. The whole craft of this recipe is texture, not flavor: eggs pulled underdone so they do not turn rubbery, potatoes actually crisped rather than steamed, and no wet ingredients inside the wrap. Salsa goes on the plate, never in the burrito.',

  ingredients: [
    {
      id: 'tortillas',
      name: 'Tortilla Land tortillas, soft taco size',
      total: '18',
      prep: 'RAW — these must be cooked before filling',
      uses: [{ step: 1, amount: '18' }],
    },
    {
      id: 'sausage',
      name: 'Breakfast sausage, bulk',
      total: '1¼ lb',
      prep: 'already seasoned — no spices needed',
      fromFreezer: true,
      uses: [{ step: 2, amount: '1¼ lb' }],
    },
    {
      id: 'hash-browns',
      name: 'Frozen shredded hash browns',
      total: 'about 13 oz',
      prep: 'half the bag, straight from frozen',
      uses: [{ step: 3, amount: 'about 13 oz' }],
    },
    {
      id: 'eggs',
      name: 'Eggs',
      total: '12',
      uses: [{ step: 4, amount: '12' }],
    },
    {
      id: 'milk',
      name: 'Whole milk',
      total: '¼ cup',
      uses: [{ step: 4, amount: '¼ cup' }],
    },
    {
      id: 'butter',
      name: 'Unsalted butter',
      total: '2 tbsp',
      uses: [{ step: 4, amount: '2 tbsp' }],
    },
    {
      id: 'spinach',
      name: 'Baby spinach',
      total: '5 oz',
      prep: 'chopped fine — the whole container',
      uses: [{ step: 5, amount: '5 oz', note: 'stirred into the hot eggs' }],
    },
    {
      id: 'cheese',
      name: 'Tillamook shredded cheese',
      total: '1½ cups',
      uses: [{ step: 6, amount: '1½ cups', note: 'a small pinch per burrito' }],
    },
    {
      id: 'salsa',
      name: 'Mild salsa',
      total: '1 jar',
      optional: true,
      optionalKind: 'garnish',
      optionalWhy:
        'Goes on the plate, never inside the wrap. Wet fillings are what make a frozen burrito soggy.',
      uses: [{ step: 8, amount: 'to taste', note: 'ON THE SIDE — never inside' }],
    },
  ],

  steps: [
    {
      n: 1,
      title: 'Cook the tortillas first',
      time: '15 min',
      critical: true,
      ingredientIds: ['tortillas'],
      body:
        'These tortillas come raw. Set a dry skillet over medium heat and cook them one at a time, about 60 seconds a side, until each one puffs up and picks up a few brown freckles. Stack them on a plate as you go and let them cool for a minute or two before you fill anything — a hot tortilla steams the filling and makes a soggy burrito.',
      tip:
        'This is the step nobody warns you about. A raw tortilla cannot be rolled or frozen — it stays gummy and the seam will not seal. The upside: a freshly cooked tortilla is far more pliable than a shelf-stable one, and it reheats noticeably better.',
    },
    {
      n: 2,
      title: 'Brown the sausage',
      time: '10 min',
      ingredientIds: ['sausage'],
      body:
        'Brown the breakfast sausage in a large skillet over medium-high, breaking it into small crumbles as it cooks, about 8 minutes. It is already seasoned — do not add anything. Drain off the fat and set the sausage aside.',
    },
    {
      n: 3,
      title: 'Crisp the hash browns',
      time: '12 min',
      critical: true,
      ingredientIds: ['hash-browns'],
      body:
        'In the same pan, spread the frozen hash browns in an even layer and then leave them completely alone for a full 5 minutes. Only after a crust has formed should you flip and break them up, then give them another 5 minutes. Season with salt and pepper and set aside.',
      tip:
        'Stirring hash browns early is the difference between crisp potato and wet shreds. They need uninterrupted contact with the pan to brown. Resist the urge.',
    },
    {
      n: 4,
      title: 'Scramble the eggs underdone',
      time: '6 min',
      critical: true,
      ingredientIds: ['eggs', 'milk', 'butter'],
      body:
        'Whisk the eggs with the milk. Melt the butter in a skillet over medium-LOW heat and add the eggs. Stir slowly and constantly, pushing them around gently. Pull them off the heat while they still look slightly wet — underdone eggs are the whole point here. Glossy, just barely set, a bit looser than you would want to eat right now; carryover cooking takes them the rest of the way.',
      tip:
        'This feels wrong and it is the most important thing in the recipe. The eggs finish cooking twice more: once in the residual heat, and again when the burrito is reheated. Eggs that look perfect now come out rubbery later.',
    },
    {
      n: 5,
      title: 'Hide the spinach',
      time: '2 min',
      ingredientIds: ['spinach'],
      body:
        'Stir the finely chopped spinach straight into the hot eggs. It wilts down in under a minute and effectively disappears — flecks of green, no texture, no flavor to speak of.',
    },
    {
      n: 6,
      title: 'Fill and roll',
      time: '15 min',
      ingredientIds: ['cheese'],
      body:
        'Lay out a cooled tortilla and add filling in a line slightly below center: eggs, sausage, hash browns, and a pinch of cheese. Aim for about ½ cup total per burrito. Fold the two sides in first, then roll up from the bottom, tucking the filling back as you go to keep it tight. Set each one seam side down.',
      tip:
        'Do not overfill. These are soft taco size, not burrito size — an 8-inch tortilla packed like a 10-inch one will not close, and a burrito that will not close will not survive the freezer.',
    },
    {
      n: 7,
      title: 'Wrap and freeze',
      time: '10 min',
      ingredientIds: [],
      body:
        'Let them cool to room temperature — warm burritos make ice crystals. Wrap each one individually and tightly in foil or plastic wrap, then collect them all into gallon freezer bags. Label the bag with the name and date.',
    },
    {
      n: 8,
      title: 'Serving',
      time: '—',
      ingredientIds: ['salsa'],
      body:
        'Salsa, hot sauce, sour cream — all of it goes on the plate alongside, never inside the wrap. Wet ingredients sealed into a burrito make the tortilla soggy in the freezer and it never recovers.',
    },
  ],

  freezing:
    'Wrap individually in foil or plastic, then bag. Keeps 3 months. Wrapping each one separately is not optional — burritos frozen touching each other fuse into a brick and tear when you pry them apart.',

  reheat: [
    {
      method: 'Thaw, then skillet',
      best: true,
      detail:
        'Thaw overnight in the fridge. Unwrap and cook in a dry skillet over medium, 4–5 minutes per side. The tortilla crisps and browns and the inside heats evenly. This is meaningfully better than every other method.',
    },
    {
      method: 'Air fryer from frozen',
      detail:
        'Unwrap, air fry at 350°F for about 10 minutes, flipping halfway. Nearly as good as the skillet and requires no planning ahead.',
    },
    {
      method: 'Oven from frozen',
      detail:
        'Leave it in the foil and bake at 375°F for 25–30 minutes. Good for reheating several at once.',
    },
    {
      method: 'Microwave',
      detail:
        'Unwrap, wrap in a damp paper towel, microwave 2–3 minutes from frozen, turning once. It works and it will be soft rather than crisp. Fine on a rushed morning.',
    },
  ],

  glossary: [
    {
      term: 'underdone eggs',
      plain:
        'Pulling scrambled eggs off the heat before they look finished. Eggs keep cooking from their own retained heat, and these get reheated later on top of that. Perfect-looking eggs now means rubbery eggs on the plate.',
    },
    {
      term: 'carryover cooking',
      plain:
        'Food continues cooking after it leaves the heat because the outside is still hot and that heat travels inward. The reason you take things off the stove early rather than when they look done.',
    },
    {
      term: 'seam side down',
      plain:
        'Rest the rolled burrito on the loose flap. The weight holds the seam shut while it cools and sets, so it does not unroll on you.',
    },
    {
      term: 'crust',
      plain:
        'The browned layer that forms where food sits still against a hot pan. It only develops with uninterrupted contact — stirring breaks it up before it can form.',
    },
  ],

  inspiration: [
    { label: 'Budget Bytes — Freezer Breakfast Burritos', url: 'https://www.budgetbytes.com/freezer-breakfast-burritos/' },
    {
      label: 'Tastes Better From Scratch — Freezer-Friendly Breakfast Burritos',
      url: 'https://tastesbetterfromscratch.com/breakfast-burritos-freezer-friendly/',
    },
  ],

  whereWeVeered:
    'Both sources use large shelf-stable burrito tortillas. We use Tortilla Land raw soft-taco tortillas, which have to be cooked first and produce 18 smaller burritos rather than 16 large ones — better portion size, and a noticeably better wrap after freezing. We also use pre-seasoned breakfast sausage instead of mixing our own spice blend, and fold chopped spinach into the eggs, which is invisible in the finished burrito.',
};
